import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CartItem, PricingError } from '@pedix-workspace/utils';
import { AnalyticsService, WebStorageService } from '@pedix-workspace/pedixapp-core-services';
import { consolidateCartItems, updateCartItemPrices } from '@pedix-workspace/orders';
import { CheckoutPreloaderService } from './features/order-checkout/checkout-preloader.service';
import { AppRequestContextService } from './app-request-context.service';

const STORAGE_NAMESPACE = 'cart-state';

@Injectable({ providedIn: 'root' })
export class AppCartItemsService {
  checkoutPreloaderService = inject(CheckoutPreloaderService);
  appRequestContextService = inject(AppRequestContextService);

  private establishmentSlug: string;
  private cartItemsBehavior = new BehaviorSubject<CartItem[]>([]);

  cartItemsObservable$ = this.cartItemsBehavior.asObservable();

  get cartItems() {
    return this.cartItemsBehavior.value;
  }

  get displaySku(): boolean {
    return this.appRequestContextService.establishment.catalogConfiguration?.displaySku || false;
  }

  private webStorage = inject(WebStorageService);
  private analyticsService = inject(AnalyticsService);

  initSession(establishmentSlug: string) {
    this.establishmentSlug = establishmentSlug;

    const cartItems = this.restoreSession(this.establishmentSlug);

    this.cartItemsBehavior = new BehaviorSubject(cartItems);
    this.cartItemsObservable$ = this.cartItemsBehavior.asObservable();
  }

  clearProducts() {
    this.storeSession(this.establishmentSlug, []);

    this.cartItemsBehavior.next([]);
  }

  addCartItem(cartItem: CartItem) {
    const cartItems = consolidateCartItems(
      [...this.cartItemsBehavior.value, cartItem],
      this.displaySku,
    );

    this.cartItemsBehavior.next(cartItems);

    this.storeSession(this.establishmentSlug, cartItems);

    this.analyticsService.addCartItem(cartItem);

    // Preloads checkout dependencies for better UX
    this.checkoutPreloaderService.preloadCheckout();
  }

  updateCartItem(cartItem: CartItem): CartItem[] {
    const cartItemIndex = this.cartItemsBehavior.value.findIndex(
      currentCartItem => currentCartItem.id === cartItem.id,
    );
    const oldCartItem = this.cartItemsBehavior.value[cartItemIndex];
    const cartItems = consolidateCartItems(
      Object.assign([], this.cartItemsBehavior.value, { [cartItemIndex]: cartItem }),
      this.displaySku,
    );

    this.cartItemsBehavior.next(cartItems);

    this.storeSession(this.establishmentSlug, cartItems);

    this.analyticsService.updateCartItem(oldCartItem, cartItem);

    return cartItems;
  }

  removeCartItem(cartItem: CartItem) {
    const cartItems = this.cartItemsBehavior.value.filter(
      currentCartItem => currentCartItem.id !== cartItem.id,
    );

    this.cartItemsBehavior.next(cartItems);

    this.storeSession(this.establishmentSlug, cartItems);

    this.analyticsService.removeCartItem(cartItem);
  }

  findCartItem(cartItemId: string): CartItem | undefined {
    return this.cartItemsBehavior.value.find(cartItem => cartItem.id === cartItemId);
  }

  updateItemPrices(pricingErrors: PricingError[]) {
    const cartItemsWithFixedPrices = updateCartItemPrices(this.cartItems, pricingErrors);

    this.cartItemsBehavior.next(cartItemsWithFixedPrices);

    this.storeSession(this.establishmentSlug, cartItemsWithFixedPrices);
  }

  private restoreSession(establishmentSlug: string): CartItem[] {
    const cartItems = this.webStorage.getItem<CartItem[]>(establishmentSlug, {
      storageType: 'session',
      prefix: 'pedixapp',
      namespace: STORAGE_NAMESPACE,
    });

    return cartItems || [];
  }

  private storeSession(establishmentSlug: string, cartItems: CartItem[]) {
    this.webStorage.setItem(establishmentSlug, cartItems, {
      storageType: 'session',
      prefix: 'pedixapp',
      namespace: STORAGE_NAMESPACE,
    });
  }
}
